import { AnalysisEngineType, AnalysisState, RecordedData } from "../types";
import {
  CompanySettingDataAtom,
  detectVolumeAtom,
  isQueryModeAtom,
  recTypeAtom,
  recordingSettingAtom,
} from "../store";
import { MutableRefObject, useCallback } from "react";
import { useGetAnalysisEngines } from "../utils/selectAnalysisEngine";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { APP_TYPE } from "../environments";
import { generateVoiceFileData } from "../utils/generateVoiceFileData";
import {
  latestMimosysAtom,
  fetchEmotions,
  fetchLatestMimosysData,
  fetchScoreSd,
  fetchScoreAve,
  isGetMimosysDataAtom,
  latestEmotionAtom,
  scoreSdAtom,
  scoreAveAtom,
} from "../stores/mimosysStore";
import { sentryLog } from "../../sentry";
import { getEmotionAverage } from "../engines/mimosysEngine";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { ApiError } from "../utils/apiClients/ApiError";

type HandleApiError = (err: unknown) => void;

function useHandleApiError(): HandleApiError {
  const navigate = useNavigate();
  return useCallback((): void => {
    // 解析エラーの場合は専用の結果画面に遷移
    navigate(`../results/error`);
  }, [navigate]);
}

function randomUnsafeStr(): string {
  // eslint-disable-next-line sonarjs/pseudo-random
  return Math.random().toString(32).substring(2);
}

type AnalyzeVoice = (
  recordedData: RecordedData[],
  requestIdRef: MutableRefObject<null | string>,
  setAnalysisStatus: (state: AnalysisState) => void,
) => Promise<void>;

export function useAnalyzeVoice(engineType: AnalysisEngineType): AnalyzeVoice {
  const handleApiError = useHandleApiError();
  const analysisEngines = useGetAnalysisEngines(engineType);
  const [detectVolume] = useAtom(detectVolumeAtom);
  const navigate = useNavigate();

  // 録音設定
  const [recordingSetting] = useAtom(recordingSettingAtom);
  // rec_type（録音種別の選択情報）のグローバル状態管理用
  const [recType] = useAtom(recTypeAtom);
  // 会社設定（rec_typesのlabel解決用）
  const [companySettingData] = useAtom(CompanySettingDataAtom);
  // クエリログインのグローバル状態管理用
  const [isQueryMode] = useAtom(isQueryModeAtom);
  // クエリログインで録音完了後に初期化の為のグローバル状態管理用
  const [, setLatestMimosys] = useAtom(latestMimosysAtom);
  const [, setScoreSd] = useAtom(scoreSdAtom);
  const [, setScoreAve] = useAtom(scoreAveAtom);
  const [, setMimosysEmotions] = useAtom(latestEmotionAtom);
  const [, setIsGetMimosysData] = useAtom(isGetMimosysDataAtom);

  return useCallback(
    async (recordedData, requestIdRef, setAnalysisStatus) => {
      setAnalysisStatus("UPLOADING");
      const requestId = randomUnsafeStr();
      requestIdRef.current = requestId;

      /*
       * ファイルアップロード
       */
      try {
        const recordingScene =
          recType > 0
            ? (companySettingData.rec_types?.find((r) => r.rec_type === recType)
                ?.label ?? recType.toString())
            : "";
        const formData = generateVoiceFileData(recordedData, recordingScene);
        if (recordingSetting?.setting_data?.keep_files) {
          formData.append("keep_file", "true");
        }
        const {
          data: uploadData,
          error: uploadError,
          response: uploadResponse,
        } = await voislogApi.POST("/v2/core/recordings/upload", {
          body: formData as never,
        });
        if (uploadError)
          throw new ApiError(uploadResponse, "post", uploadError);
        const recordingId = uploadData.recording_id;

        /*
         * 音声解析
         */
        const requests = analysisEngines.map((engine) =>
          engine.analyze_call(recordingId, detectVolume),
        );

        setAnalysisStatus("ANALYZING");
        const analysisResponse = await Promise.allSettled(requests);

        if (!isQueryMode) {
          /*
           * 一般ユーザーによる通常ログインの場合
           */
          // 解析結果確認
          const isAllFailed = analysisResponse.every(
            (result) => result.status === "rejected",
          );
          if (isAllFailed) {
            handleApiError(
              (analysisResponse[0] as PromiseRejectedResult).reason,
            );
            setAnalysisStatus("IDLE");
          } else if (requestIdRef.current === requestId) {
            setIsGetMimosysData(false);

            // 解析結果の画面に遷移
            const fetchResults = async (): Promise<void> => {
              const [latestMimosys, scoreSd, scoreAve] = await Promise.all([
                fetchLatestMimosysData(),
                fetchScoreSd(),
                fetchScoreAve(),
              ]);
              setLatestMimosys(latestMimosys);
              setScoreSd(scoreSd);
              setScoreAve(scoreAve);
              setIsGetMimosysData(true);

              // PoCアプリの場合は感情の解析結果が必要
              if (APP_TYPE === "poc") {
                const target_emotions = await fetchEmotions({
                  recording_id: recordingId,
                });
                if (target_emotions) {
                  const averageEmotions = getEmotionAverage(
                    target_emotions.emotions,
                  );
                  setMimosysEmotions(averageEmotions);
                }
              }

              navigate(`../results/${recordingId}`);
            };

            fetchResults()
              .catch((err) => {
                handleApiError(err);
                sentryLog(err);
              })
              .finally(() => {
                setAnalysisStatus("IDLE");
              });
          }
        } else {
          /*
           * 契約者アカウントによるクエリログインの場合
           */
          // クエリログイン時の音声録音解析完了画面に遷移
          navigate("../../../query-login-result");
        }
      } catch (err) {
        handleApiError(err);
        sentryLog(err);
      } finally {
        if (isQueryMode) {
          setAnalysisStatus("IDLE");
        }
      }
    },
    [
      analysisEngines,
      companySettingData,
      detectVolume,
      handleApiError,
      navigate,
      recType,
      isQueryMode,
      setIsGetMimosysData,
      setLatestMimosys,
      setScoreSd,
      setScoreAve,
      setMimosysEmotions,
      recordingSetting,
    ],
  );
}
